// External dependencies
import React, { Suspense, useEffect, useState } from 'react'
import { Provider } from 'react-redux'
import { Router } from 'react-router-dom'
import { history, store } from 'store'
import { BreakpointProvider } from 'utils/hoc/breakpointProvider'
import ScrollToTop from 'utils/hoc/scrollToTop'
import PathByI18nSwitcher from 'components/PathByI18nSwitcher'
import Seo from 'components/Seo'
import SentryManager from 'components/SentryProvider'
import { AuthProvider, AppRouter } from 'modules/Auth'

const CookieBanner = React.lazy(() => import('components/CookieBanner'))
const Modal = React.lazy(() => import('modules/Modal'))
const Notifications = React.lazy(() => import('modules/Notifications'))
const OnboardingManager = React.lazy(() => import('modules/Onboarding/OnboardingManager'))
const DevToolsPanel =
  process.env.NODE_ENV !== 'production'
    ? React.lazy(() => import('components/DevToolsPanel'))
    : null

const DEV_PANEL_STORAGE_KEY = 'dev_panel'

const isDevPanelEnabled = () =>
  process.env.NODE_ENV !== 'production' && localStorage.getItem(DEV_PANEL_STORAGE_KEY) === 'true'

const DevToolsGate = () => {
  const [enabled, setEnabled] = useState(isDevPanelEnabled)

  useEffect(() => {
    const sync = () => setEnabled(isDevPanelEnabled())

    window.addEventListener('storage', sync)
    window.addEventListener('dev-panel-storage-changed', sync)

    return () => {
      window.removeEventListener('storage', sync)
      window.removeEventListener('dev-panel-storage-changed', sync)
    }
  }, [])

  if (!enabled || !DevToolsPanel) {
    return null
  }

  return (
    <Suspense fallback={<></>}>
      <DevToolsPanel />
    </Suspense>
  )
}

const App = () => (
  <Provider store={store}>
    <Router history={history}>
      <PathByI18nSwitcher />
      <Seo />
      <BreakpointProvider>
        <AuthProvider>
          <AppRouter />
        </AuthProvider>
        <Suspense fallback={<></>}>
          <Modal />
        </Suspense>
        <Suspense fallback={<></>}>
          <OnboardingManager />
        </Suspense>
        <Suspense fallback={<></>}>
          <CookieBanner />
        </Suspense>
      </BreakpointProvider>
      <Suspense fallback={<></>}>
        <Notifications />
      </Suspense>
      <DevToolsGate />
      <ScrollToTop />
      <SentryManager />
    </Router>
  </Provider>
)

export default App

export { default as Reducer } from './store/reducer'
export { Sagas } from './store/saga'
